import React, { MouseEvent, MutableRefObject, useEffect, useRef, useState } from 'react';
import { SkeletonCard } from '@noths/polaris-client-ribbons-design-system';
import { grid, visuallyHidden } from '@noths/polaris-client-styles';
import { getPagePosition, getProductIdFromProductCode } from '@noths/polaris-client-utils';

import { ProductsViewed } from 'src/components/atoms/ProductsViewed';
import { ProductCard } from 'src/components/molecules/ProductCard';
import { UpdateHistoryPayload } from 'src/redux/products/thunks';
import { DecoratedProductForDisplay, Product, ProductUrlType } from 'src/types';
import { INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING } from '../constants/pageConfiguration';
import { LOAD_MORE__BUTTON, LOAD_PREVIOUS__BUTTON } from '../constants/testIds';
import { LoadButton } from './LoadButton';
import { ProgressBar } from './ProgressBar';
import * as styles from '../styles/ProductPage.styles';
export const PRODUCT_CARD_GRID_ITEM__DIV = 'PRODUCT_CARD_GRID_ITEM__DIV';

type ProductCode = Product['code'];

export interface ProductPageProps {
  page: number;
  currentPage: number;
  totalPages: number;
  products: DecoratedProductForDisplay[];
  totalNumberOfProducts: number;
  elevaateProductImpressions: (products: Product[]) => void;
  isPageLoading: boolean;
  onProductClick: (
    productUrl: ProductUrlType,
    destinationUrl: ProductUrlType,
    productCode: ProductCode,
  ) => void;
  isLoading: boolean;
  isClientRequest?: boolean;
  progress: number;
  numberOfProductsViewed: number;
  isFirstDisplayedPage: boolean;
  isLastDisplayedPage: boolean;
  loadMoreURL: string;
  loadPreviousURL: string;
  referredBy?: string;
  onLoadMore: VoidFunction;
  onLoadPrevious: VoidFunction;
  updateFavourite: (product: ProductCode) => void;
  updatePageHistory: (payload: UpdateHistoryPayload) => void;
}

export const ProductPage = ({
  updatePageHistory,
  page,
  currentPage,
  totalPages,
  products,
  totalNumberOfProducts,
  elevaateProductImpressions,
  isPageLoading,
  isLoading,
  progress,
  numberOfProductsViewed,
  isFirstDisplayedPage,
  isLastDisplayedPage,
  referredBy,
  loadMoreURL,
  loadPreviousURL,
  onLoadMore,
  onLoadPrevious,
  updateFavourite,
  onProductClick,
  ...props
}: ProductPageProps) => {
  const lastProduct: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const [isClientRequest] = useState(props.isClientRequest);

  const handleClickLoadPrevious = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onLoadPrevious();
  };

  const handleClickLoadMore = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onLoadMore();
  };

  const handleProductClickImpression = (
    destinationUrl: ProductUrlType,
    productCode: ProductCode,
    productOffset: number,
  ) => {
    const productUrl = referredBy ? `${destinationUrl}?referredBy=${referredBy}` : destinationUrl;
    updatePageHistory({ page, productCode, productOffset });
    onProductClick(productUrl, destinationUrl, productCode);
  };

  useEffect(() => {
    // Register elevaate product impressions
    elevaateProductImpressions(products);
  }, [products, elevaateProductImpressions]);

  useEffect(() => {
    if (page < currentPage && isFirstDisplayedPage) {
      window.scrollTo(0, getPagePosition(lastProduct.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid={`product-page-${page}`}
      css={[grid, styles.productPage, isClientRequest && styles.fadeIn]}
      aria-hidden={isPageLoading}
    >
      {isFirstDisplayedPage && page > 1 ? (
        <div css={styles.loadPreviousWrapper}>
          <LoadButton
            testId={LOAD_PREVIOUS__BUTTON}
            css={styles.loadPreviousButton}
            isLoading={isLoading}
            linkUrl={loadPreviousURL}
            onClick={handleClickLoadPrevious}
          >
            <span>
              Load previous<span css={visuallyHidden}> products</span>
            </span>
          </LoadButton>
        </div>
      ) : null}
      {products.map((product, i) => (
        <div
          css={styles.productCardGridItem}
          key={`${product.code}-${product.isSponsored}`}
          ref={i === products.length - 1 ? lastProduct : null}
          data-testid={PRODUCT_CARD_GRID_ITEM__DIV}
          data-productid={getProductIdFromProductCode(product.code)}
        >
          {isPageLoading ? (
            <SkeletonCard productIndex={i} />
          ) : (
            <ProductCard
              {...product}
              updateFavourite={updateFavourite}
              onProductClick={handleProductClickImpression}
              productIndex={i}
              disableImageLazyLoading={i < INITIAL_NUMBER_OF_PRODUCTS_TO_DISABLE_LAZY_LOADING}
            />
          )}
        </div>
      ))}
      {isLastDisplayedPage ? (
        <div css={styles.paginationWrapper}>
          <ProductsViewed
            numberOfProductsViewed={numberOfProductsViewed}
            totalNumberOfProducts={totalNumberOfProducts}
          />
          <ProgressBar css={styles.progressBar} progress={progress} />
          {page < totalPages ? (
            <LoadButton
              testId={LOAD_MORE__BUTTON}
              isLoading={isLoading}
              linkUrl={loadMoreURL}
              onClick={handleClickLoadMore}
            >
              <span>
                Load more<span css={visuallyHidden}> products</span>
              </span>
            </LoadButton>
          ) : null}
        </div>
      ) : (
        <ProductsViewed
          numberOfProductsViewed={numberOfProductsViewed}
          totalNumberOfProducts={totalNumberOfProducts}
          isDivider={true}
        />
      )}
    </div>
  );
};
